// joonyi edit
import React, { useState, useRef, useMemo, useEffect } from 'react';
// import JSZip from 'jszip';  // not needed now
import { FilePlus, X } from 'lucide-react';
import * as pdfjsLib from "pdfjs-dist/webpack";

async function getPdfPageCount(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  return pdf.numPages;
}

export async function getPresignedUrl(fileName, contentType = 'application/pdf') {
  const res = await fetch("https://4mcoq5zpn5raailhhjvydy6nmm0wspqa.lambda-url.ap-southeast-1.on.aws/", {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName, contentType }),
  });
  if (!res.ok) throw new Error(`Presign failed: ${res.status}`);
  return res.json(); // { uploadUrl, bucket, key, contentType, expiresIn }
}

export async function uploadFile(file) {
  const { uploadUrl, key, bucket, contentType } = await getPresignedUrl(
    file.name,
    file.type || 'application/pdf'
  );
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType }, // MUST match what was signed
    body: file,                                // <-- BYTES
  });
  if (!put.ok) throw new Error(`Upload failed: ${put.status}`);
  return { bucket, key };
}



async function sendExtractedDataToLambda({ slides_data, lambdaEndpoint }) {
  const payload = {
    slides_data,
  };

  const res = await fetch(lambdaEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    // Not JSON → surface status + body so you can see the cause
    throw new Error(`Lambda ${res.status}: ${text}`);
  }
  if (!res.ok) {
    throw new Error(data?.error || `Lambda ${res.status}`);
  }
  return data;
}


export default function PDFUpload(props) {
  const {
    isOpen,
    title = "Upload PDF → PPTX",
    folder = "uploads",
    presignEndpoint,
    state: externalState,
    setState: externalSetState,
    onDone,
    onNext,
    onBack,
    onClose,
    step,
  } = props;

  // Internal fallback if parent doesn't provide state
  const [internalState, internalSetState] = useState({
    fileName: "",
    msg: "",
    busy: false,
    s3: null,
  });

  const [result, setResult] = useState(null);

  const state = externalState ?? internalState;
  const setState =
    externalSetState ??
    ((updater) =>
      internalSetState((prev) =>
        typeof updater === "function" ? updater(prev) : updater
      ));

  // Destructure with defaults to avoid undefined
  const { fileName = "", msg = "", busy = false } = state;
  const canUpload = !busy;
  const setMsg  = (m) => setState(s => ({ ...s, msg: m }));
  const setBusy = (b) => setState(s => ({ ...s, busy: b }));
  const setFile = (n) => setState(s => ({ ...s, fileName: n }));

  const isPdf = (f) => !!f && /\.pdf$/i.test(f.name || "");

  async function doUpload(file) {
    try {
      setBusy(true);
      setMsg("Checking PDF...")
      setResult("")

      const pageCount = await getPdfPageCount(file);
      if (pageCount > 30) {
        setMsg(`❌ Too many pages (${pageCount}). Max allowed is 30.`);
        setBusy(false);
        return;
      }

      setMsg("Uploading to Lambda…");
      setFile(file.name);

      const { bucket, key } = await uploadFile(file);

      const processRes = await fetch(
        "https://wbqb7tz6lomttsfgvvkziwklhm0cbdms.lambda-url.ap-southeast-1.on.aws/", // your extract endpoint
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "extract",
            s3Bucket: bucket,
            s3Key: key,
            fileName: file.name
          }),
        }
      );
      const processText = await processRes.text();
      if (!processRes.ok) throw new Error(`Lambda ${processRes.status}: ${processText}`);
      const processData = JSON.parse(processText);

      const openAIAnalysis = processData?.openAIAnalysis?.analysis;
      const slides_data = openAIAnalysis?.suggestedSlides || [];

      setMsg("✅ Uploaded & processed. Generating PowerPoint...");

      const pptx_res = await sendExtractedDataToLambda({ slides_data: slides_data, lambdaEndpoint: "https://t7bn57u42bcwown2ru5liywr2y0ptnfl.lambda-url.ap-southeast-1.on.aws/" });
      setResult(pptx_res)
      setMsg("")
      onDone && onDone(processRes, file);
    } catch (err) {
      console.error(err);
      setMsg(`❌ ${err.message || "Upload failed"}`);
      setFile("");
    } finally {
      setBusy(false);
    }
  }

  async function handlePick(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 40 * 1024 * 1024;
    if (file.size > maxSize) {
      setMsg(`❌ File too large (${(file.size / 1024 / 1024).toFixed(2)} MB). Max allowed is 40 MB.`);
      return;
    }

    if (!isPdf(file)) return setMsg("Please select a .pdf file.");
    if (fileName) return setMsg("❌ One file already selected. Remove it to upload another.");
    await doUpload(file);
  }

  async function handleDrop(e) {
    e.preventDefault();
    if (busy) return;
    const files = Array.from(e.dataTransfer.files || []).filter(isPdf);
    if (files.length === 0) return setMsg("❌ Please drop a .pdf file.");
    if (files.length > 1) return setMsg("❌ Please drop only one .pdf file.");
    if (fileName) return setMsg("❌ One file already selected. Remove it to upload another.");
    await doUpload(files[0]);
  }

  function removeFile() {
    if (busy) return;
    setState(s => ({ ...s, fileName: "", msg: "", s3: null }));
  }

  return (
    isOpen && (
      <div className="files-upload-popup">
        <div className="files-popup-content">
                <button
                  className="close-button close-button--abs"
                  onClick={onClose}
                  disabled={busy} style={{ cursor: busy ? "not-allowed" : "pointer" }}
                >
                  <X size={18} />
                </button>
            <div className="file-dropzone" onDragOver={(e)=>e.preventDefault()} onDrop={handleDrop}>
            <div className='flex justify-between p-4'>
                <h2 className='font-semibold text-white text-xl'>{title}</h2>
            </div>

            <div className={`container-dropzone ${busy ? "processing" : ""}`} style={{ marginTop: 12 }}>
                <input
                id="pdf-input"
                type="file"
                accept="application/pdf,.pdf"   // <-- PDF only
                multiple={false}
                style={{ display: "none" }}
                onChange={handlePick}
                disabled={busy}
                />

                <div 
            className="file-dropzone"
            onClick={() => {
              if (!busy) {
                document.getElementById("pdf-input").click();
              }
            }}
            style={{ 
              opacity: busy ? 0.6 : 1, 
              cursor: busy ? 'not-allowed' : 'pointer' 
            }}
          >
            <FilePlus size={32} className="file-icon" />
            <span>
              {busy 
                ? 'Uploading file...' 
                : 'Drag & Drop or Click to Upload'
              }
            </span>
            
                    <div className="file-requirements">
                        <ul className="file-disclaimers">
                            <li><strong>What it does:</strong> Summarizes your PDF into a formatted PowerPoint presentation.</li>
                            <li><strong>Supported:</strong> PDF documents only. One file at a time.</li>
                            <li><strong>Max File Size:</strong> 40MB</li>
                            <li><strong>Max Pages of PDF:</strong> 30 pages</li>
                        </ul>
                    </div>
                </div>
            </div>

            {fileName && (
                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <span style={{ fontWeight: 500 }}>{fileName}</span>
                <button className='bg-red-500 rounded-full px-2 py-1 text-white hover:bg-red-700' onClick={removeFile} disabled={busy} title="Remove" style={{ cursor: busy ? "not-allowed" : "pointer" }}>Remove</button>
                </div>
            )}

            {msg && (
                <div style={{ marginTop: 10, fontSize: 14, color: msg.startsWith("❌") ? "#b00020" : "#555" }}>
                {msg}
                </div>
            )}

            {result && (
                <div className="success">
                    <p>✅ Done! Your file is ready.</p>
                    <a
                        href={result.downloadUrl}
                        download
                        rel="noopener"
                        target="_blank"
                        className="btn-primary"
                    >
                        Download PPTX
                    </a>
                {/* If you want to keep a permanent reference */}
                {/* <code>{result.out_key}</code> */}
                </div>
            )}

            <div className="popup-actions wizard-nav">
                {/* {step !== 1 && <button className="btn-secondary" onClick={onBack} disabled={busy}>◀ Back</button>} */}
                {/* <button className="btn-secondary" onClick={onClose} disabled={busy} style={{ cursor: busy ? "not-allowed" : "pointer" }}>Close</button> */}
                {/* <button onClick={onNext} disabled={!fileName || busy}>Next ▶</button> */}
            </div>

            </div>
        </div>
    </div>
    )
  );
}


